package pl.finn.forms.utils.model

import io.kvision.core.Component
import io.kvision.html.ButtonStyle
import io.kvision.html.Div
import io.kvision.html.button
import io.kvision.panel.SimplePanel
import pl.finn.forms.tw1.*
import pl.finn.forms.utils.Components
import pl.finn.forms.utils.FormReadingUtils
import pl.finn.forms.utils.form.FormBuilder

class Addresses(reference: FormBuilder<DocumentTreeNode>) {
  private val formReference = reference

  private val components = Components(formReference)

  fun createAddress(addressNode: DocumentTreeNode, address: Adres): ArrayList<Any> {
    val adresPolski = createPolandAddress(address.adresPolski)
    val adresZagraniczny = ArrayList<Any>()
    adresZagraniczny.add(
      components.createSelect(
        address.adresZagranicznyKodKraju,
        "Kod Kraju",
        Tw1Data.listOfCountryCodes,
        true
      )
    )
    adresZagraniczny.add(
      components.createInput(
        address.adresZagranicznyKodPocztowy,
        "Kod pocztowy",
        isRequired = false
      )
    )
    adresZagraniczny.add(
      components.createInput(
        address.adresZagranicznyMiejscowosc,
        "Nazwa miejscowości"
      )
    )
    adresZagraniczny.add(
      components.createInput(
        address.adresZagranicznyUlica,
        "Ulica",
        isRequired = false
      )
    )
    adresZagraniczny.add(
      components.createInput(
        address.adresZagranicznyNrDomu,
        "Numer budynku",
        isRequired = false,
        requirements = FormReadingUtils.checkValidation("TNrBudynkuTAdresZagraniczny")
      )
    )
    adresZagraniczny.add(
      components.createInput(
        address.adresZagranicznyNrLokalu,
        "Numer lokalu",
        isRequired = false
      )
    )
    val adresMap = HashMap<String, Any?>()
    adresMap["0"] = arrayListOf<Any>(components.createFieldset("Adres polski"), adresPolski)
    adresMap["1"] = arrayListOf<Any>(components.createFieldset("Adres zagraniczny"), adresZagraniczny)
    return arrayListOf<Any>(
      components.createRadioGroup(
        addressNode,
        "Adres",
        Tw1Data.optionsOfAdress,
        adresMap
      ),
      adresMap
    )
  }

  fun createPolandAddress(address: AdresPolski): ArrayList<Any> {
    val adresPolski = ArrayList<Any>()
    adresPolski.add(
      components.createInput(
        address.wojewodztwo,
        "Województwo"
      )
    )
    adresPolski.add(
      components.createInput(
        address.powiat,
        "Powiat"
      )
    )
    adresPolski.add(
      components.createInput(
        address.gmina,
        "Gmina"
      )
    )
    adresPolski.add(
      components.createInput(
        address.miejscowosc,
        "Nazwa miejscowości"
      )
    )
    adresPolski.add(
      components.createInput(
        address.ulica,
        "Nazwa ulicy",
        isRequired = false
      )
    )
    adresPolski.add(
      components.createInput(
        address.nrDomu,
        "Numer budynku",
        requirements = FormReadingUtils.checkValidation("TNrBudynkuTAdresPolski")
      )
    )
    adresPolski.add(
      components.createInput(
        address.nrLokalu,
        "Numer lokalu",
        isRequired = false
      )
    )
    val postCode = components.createInput(
      address.kodPocztowy,
      "Kod pocztowy"
    )
    postCode.field.input.setAttribute("placeholder", "__-___")
    postCode.field.input.setAttribute("data-slots", "_")
    postCode.field.input.setAttribute("data-accept", "\\d")
    postCode.field.input.setAttribute("size", "5")
    postCode.field.input.setAttribute("onfocus", "FormUtils.applyDataMask(this);")
    adresPolski.add(postCode)
    return adresPolski
  }

  fun createPositions(pozycja: ArrayList<Pozycja>, legend: String, odsetki: Boolean): ArrayList<Any> {
    val finnId = "collapseFinn_" + Util.getRandomString()
    val div = Div() { id = finnId }
    val position = ArrayList<Any>()
    val positionData = ArrayList<Any>()

    pozycja.forEach {
      positionData.add(createNewPosition(it, legend, odsetki))
    }

    position.add(div)
    position.add(arrayListOf(
      positionData,
      createButtonGroup(pozycja, div, legend, odsetki),
    ))
    return position
  }

  private fun createNewPosition(pozycja: Pozycja, legend: String, odsetki: Boolean): ArrayList<Any> {
    val position = ArrayList<Any>()
    val positionData = ArrayList<Any>()
    position.add(components.createFieldset(legend))
    positionData.add(
      components.createInput(
        pozycja.kwota,
        "Kwota należności pieniężnej",
        tooltipMessage = "Wymagana wartość: <b>xx.xx</b><br/><br/>Kwoty należności pieniężnych, w tym odsetek podaje się w złotych i groszach zaokrąglonych zgodnie z art. 27a § 3 ustawy z dnia 17 czerwca 1966 r. o postępowaniu egzekucyjnym w administracji (Dz. U. z 2012 r. poz. 1015, z późn. zm.), zwanej dalej „ustawą”.",
        requirements = FormReadingUtils.checkValidation("TKwota2Nieujemna")
      )
    )
    if (odsetki) {
      positionData.add(
        components.createDateTime(
          pozycja.odsetkiDniaOd,
          "Data, od której nalicza się odsetki",
          isRequired = true
        )
      )
    }
    positionData.add(
      components.createInput(
        pozycja.kwotaOdsetek,
        "Wysokość odsetek na dzień wystawienia tytułu wykonawczego",
        tooltipMessage = "Wymagana wartość: <b>xx.xx</b><br/><b>W przypadku wpisania kwoty musi być ona większa od zera</b> <br/><br/>Kwoty należności pieniężnych, w tym odsetek podaje się w złotych i groszach zaokrąglonych zgodnie z art. 27a § 3 ustawy z dnia 17 czerwca 1966 r. o postępowaniu egzekucyjnym w administracji (Dz. U. z 2012 r. poz. 1015, z późn. zm.), zwanej dalej „ustawą”.",
        requirements = FormReadingUtils.checkValidation("TKwota2Nieujemna")
      )
    )
    positionData.add(
      components.createDateTime(
        pozycja.dochodzicDniaDo,
        "Data, do której należność pieniężna może być dochodzona",
        isRequired = false
      )
    )
    positionData.add(
      components.createFieldset(
        "Data powstania należności pieniężnej/okres, którego dotyczy należność pieniężna"
      )
    )
    positionData.add(
      arrayListOf(
        components.createDateTime(
          pozycja.okresDniaOd,
          "Od dnia"
        ),
        components.createDateTime(
          pozycja.okresDniaDo,
          "Do dnia",
          isRequired = false
        )
      )
    )
    positionData.add(
      components.createInput(
        pozycja.podstawaPrawnaPierwszenstwa,
        "Podstawa prawna pierwszeństwa zaspokojenia należności pieniężnej",
        isRequired = false
      )
    )
    position.add(positionData)
    return position
  }

  fun createButtonGroup(
    parentElements: ArrayList<Pozycja>,
    div: Div,
    legend: String,
    odsetki: Boolean
  ): Div {
    return (
        Div(className = "p-2 d-flow-root") {
          if (parentElements.size > 1) {
            button(
              "Usuń",
              "far fa-minus-square",
              ButtonStyle.OUTLINEPRIMARY, className = "btn-sm"
            ).onClick {
              formReference.getData()

              formReference.form.remove(parentElements.get(parentElements.lastIndex).podstawaPrawnaPierwszenstwa)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).odsetkiDniaOd)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).dochodzicDniaDo)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).kwota)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).kwotaOdsetek)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).okresDniaDo)
              formReference.form.remove(parentElements.get(parentElements.lastIndex).okresDniaOd)
              parentElements.removeAt(parentElements.lastIndex)
              val partners = ArrayList<Any>()

              parentElements.forEach {
                partners.add(createNewPosition(it, legend, odsetki))
              }
              div.getChildren().forEach {
                components.changeElementChildrenStatus(it, false)
                (it as? SimplePanel?)?.hide()
                formReference.remove(it)
              }
              div.removeAll()

              components.readComponentsList(partners, div)
              components.readComponentsList(createButtonGroup(parentElements, div, legend, odsetki), div)
            }
          }
          button(
            "Dodaj",
            "far fa-plus-square",
            ButtonStyle.OUTLINEPRIMARY, className = "btn-sm float-right"
          ).onClick {
            formReference.getData()
            parentElements.add(Pozycja())
            val partners = ArrayList<Any>()

            parentElements.forEach {
              partners.add(createNewPosition(it, legend, odsetki))
            }

            div.removeAll()
            components.readComponentsList(partners, div)
            components.readComponentsList(createButtonGroup(parentElements, div, legend, odsetki), div)
          }
        }
        )
  }
}
